import { InputAdornment } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { systemConfigKeyEnum } from '../../../../constants';
import { useCreateDailyTempWorkerMutation, useDailyTempWorkerMutation } from '../../../../hooks/mutations/useDailyTempWorkerMutation';
import { useTempWorkersQuery } from '../../../../hooks/queries/useTempWorkersQuery';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { DailyTempWorker } from '../../../../services/apiSLTP/dailyReports/dailyTempWorkers';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import IconButton from '../../../ui/IconButton';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageValidationDailyTempWorkerModalProps extends ModalProps {
    refetch: () => void;
    dailyTempWorker: DailyTempWorker | null;
    activities: OptionType[];
    siteName?: string;
}

const ManageValidationDailyTempWorkerModal: React.FC<ManageValidationDailyTempWorkerModalProps> = ({
    open,
    handleClose,
    refetch,
    dailyTempWorker,
    activities,
    siteName,
}) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [currentDailyTempWorkerId] = useState(dailyTempWorker?._id || '');
    const [unitPrice, setUnitPrice] = useState(dailyTempWorker?.unitPrice?.toString() || '');
    const [tempWorkerName, setTempWorkerName] = useState(dailyTempWorker?.tempWorkerName || '');
    const [hours, setHours] = useState(dailyTempWorker?.durationHours?.toString() || '7');
    const [selectedTempWorkerType, setSelectedTempWorkerType] = useState({
        label: dailyTempWorker?.tempWorkerTypeId.designation || '',
        value: dailyTempWorker?.tempWorkerTypeId._id || '',
    });
    const [selectedActivity, setSelectedActivity] = useState({
        label: activities.find((activity) => activity.value === dailyTempWorker?.activityId)?.label || '',
        value: dailyTempWorker?.activityId || '',
    });
    //const [quantity, setQuantity] = useState<number>(dailyTempWorker?.durationHours ?? 7);

    const { data: dataTempWorkers } = useTempWorkersQuery({ queries: { active: true } });

    const { mutate: createDailyTempWorker } = useCreateDailyTempWorkerMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });
    const { mutate: updateDailyTempWorker } = useDailyTempWorkerMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const postDailyTempWorker = async () => {
        createDailyTempWorker({
            body: {
                activityId: selectedActivity.value,
                tempWorkerTypeId: selectedTempWorkerType.value,
                tempWorkerName,
                durationHours: parseFloat(hours),
                unitPrice: unitPrice.length ? parseFloat(unitPrice) : undefined,
            },
        });
    };

    const patchDailyTempWorker = async () => {
        if (currentDailyTempWorkerId)
            updateDailyTempWorker({
                id: currentDailyTempWorkerId,
                body: {
                    activityId: selectedActivity.value,
                    tempWorkerTypeId: selectedTempWorkerType.value,
                    tempWorkerName,
                    durationHours: parseFloat(hours),
                    unitPrice: unitPrice.length ? parseFloat(unitPrice) : null,
                },
            });
    };

    return (
        <ModalBody
            title={t('tempWorker')}
            subtitle={!!currentDailyTempWorkerId.length ? t('update') : t('add')}
            description={siteName}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                !currentDailyTempWorkerId.length ? postDailyTempWorker() : patchDailyTempWorker();
            }}
            disableSubmit={!selectedTempWorkerType.value.length || !selectedActivity.value.length || !tempWorkerName.length || !hours.length}
        >
            <DropDown
                selectedItem={selectedActivity}
                items={activities}
                handleChange={(event, value) => setSelectedActivity({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('activityOf')}
                required
            />
            <DropDown
                selectedItem={selectedTempWorkerType}
                items={
                    dataTempWorkers?.map((tempWorker) => ({
                        label: tempWorker.designation,
                        value: tempWorker._id,
                    })) || []
                }
                handleChange={(event, value) => setSelectedTempWorkerType({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('company')}
                required
            />
            <CustomTextInput value={tempWorkerName} onChange={(value) => setTempWorkerName(value)} label={t('name')} required />
            <CustomTextInput
                label={capitalizeFirstLetter(t('durationHours'))}
                type="number"
                value={hours}
                onChange={(text) => setHours(text)}
                required
                startAdornment={
                    <InputAdornment position="start">
                        <IconButton
                            iconName="remove"
                            disabled={parseFloat(hours) <= 0}
                            onClick={() => setHours(Math.max(0, (parseFloat(hours) || 0.5) - 0.5).toString())}
                        />
                    </InputAdornment>
                }
                endAdornment={
                    <InputAdornment position="end">
                        <IconButton
                            iconName="add"
                            disabled={parseFloat(hours) >= 24}
                            onClick={() => setHours(Math.min(24, (parseFloat(hours) || 0) + 0.5).toString())}
                        />
                    </InputAdornment>
                }
            />
            {/*<QuantityInput
                value={dailyTempWorker.durationHours ?? 0}
                onChange={(newValue) => updateDailyTempWorker({ id: dailyTempWorker._id, body: { durationHours: newValue } })}
                unit="H"
                min={0}
                max={24}
                step={0.5}
                color="success"
                size="small"
            />*/}

            <CustomTextInput
                value={unitPrice}
                onChange={setUnitPrice}
                label={t('unitPrice')}
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
        </ModalBody>
    );
};

export default ManageValidationDailyTempWorkerModal;
